/**
 * canvas-engine/presets/decompositionAdapters/index.js
 *
 * Aggregated decomposition adapters + the family dispatch helpers.
 *
 * The own family/chrome/custom/dynamic adapters (Task 3.3) each turn a
 * transcribed preset DATA descriptor — OR a bare section-type string looked up
 * in the family's transcribed presetData map — into a normalized, fully
 * editable Canvas_Model by delegating to the core `buildSectionCanvas` engine
 * with family-specific hooks where the default vertical-stack layout is not
 * faithful enough.
 *
 * `resolveAdapter` / `decomposeSection` route a section type to the right
 * family adapter; they are consumed by the Canvas_Preset registry (Task 3.4)
 * and the section migration (Task 4.1). Each adapter also exports the set of
 * section types it handles (`*_TYPES`) so dispatch can be validated by type.
 *
 * Type → family mapping (by the same prefixes the domains use):
 *   - header, footer                                  → chrome
 *   - customHtml, customCss, custom*                  → custom
 *   - dynamic*, products, courses, bookings, cart     → dynamic
 *   - education*                                      → education
 *   - waas*                                           → waas
 *   - flexOffice*                                     → flexOffice
 *   - retail*                                         → retail
 *   - everything else (normal / unknown)              → generic
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules + transcribed
 * presetData VALUES. Never emits a `native` element.
 *
 * Requirements: 4.1, 4.2, 4.3, 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 17.1, 17.2,
 * 17.3, 25.1, 25.4
 */

import buildSectionCanvas from './buildSectionCanvas';
import { educationToCanvas, EDUCATION_TYPES } from './education';
import { waasToCanvas, WAAS_TYPES } from './waas';
import { flexOfficeToCanvas, FLEX_OFFICE_TYPES } from './flexOffice';
import { retailToCanvas, RETAIL_TYPES } from './retail';
import { genericToCanvas, GENERIC_TYPES } from './generic';
import { chromeToCanvas, CHROME_TYPES } from './chrome';
import { customToCanvas, CUSTOM_TYPES } from './custom';
import { dynamicToCanvas, DYNAMIC_TYPES } from './dynamic';

export {
  buildSectionCanvas,
  educationToCanvas,
  waasToCanvas,
  flexOfficeToCanvas,
  retailToCanvas,
  genericToCanvas,
  chromeToCanvas,
  customToCanvas,
  dynamicToCanvas,
  EDUCATION_TYPES,
  WAAS_TYPES,
  FLEX_OFFICE_TYPES,
  RETAIL_TYPES,
  GENERIC_TYPES,
  CHROME_TYPES,
  CUSTOM_TYPES,
  DYNAMIC_TYPES,
};

/** The family keys recognized by the dispatch helpers. */
export const ADAPTER_FAMILIES = Object.freeze([
  'education',
  'waas',
  'flexOffice',
  'retail',
  'chrome',
  'custom',
  'dynamic',
  'generic',
]);

/** Family → adapter function map. */
const ADAPTERS = Object.freeze({
  education: educationToCanvas,
  waas: waasToCanvas,
  flexOffice: flexOfficeToCanvas,
  retail: retailToCanvas,
  chrome: chromeToCanvas,
  custom: customToCanvas,
  dynamic: dynamicToCanvas,
  generic: genericToCanvas,
});

/** Family → handled section types map (the adapter-exported coverage lists). */
export const ADAPTER_TYPE_COVERAGE = Object.freeze({
  education: EDUCATION_TYPES,
  waas: WAAS_TYPES,
  flexOffice: FLEX_OFFICE_TYPES,
  retail: RETAIL_TYPES,
  chrome: CHROME_TYPES,
  custom: CUSTOM_TYPES,
  dynamic: DYNAMIC_TYPES,
  generic: GENERIC_TYPES,
});

/** Bare dynamic section types (the DYNAMIC_PRESETS keys). */
const DYNAMIC_KEYS = new Set(['products', 'courses', 'bookings', 'cart']);

function startsWith(type, prefix) {
  return typeof type === 'string' && type.length >= prefix.length && type.slice(0, prefix.length) === prefix;
}

/**
 * Resolve a section type to its family key. Order matters: chrome, custom, and
 * dynamic are matched before the family prefixes so that, e.g., `educationCart`
 * stays in the education family while the bare `cart` dynamic type routes to
 * dynamic.
 *
 * @param {string} sectionType
 * @returns {string} one of ADAPTER_FAMILIES
 */
export function familyForType(sectionType) {
  const type = typeof sectionType === 'string' ? sectionType.trim() : '';
  if (!type) return 'generic';

  if (type === 'header' || type === 'footer') return 'chrome';
  if (startsWith(type, 'custom')) return 'custom';
  if (startsWith(type, 'dynamic') || DYNAMIC_KEYS.has(type)) return 'dynamic';
  if (startsWith(type, 'education')) return 'education';
  if (startsWith(type, 'waas')) return 'waas';
  if (startsWith(type, 'flexOffice')) return 'flexOffice';
  if (startsWith(type, 'retail')) return 'retail';
  return 'generic';
}

/**
 * Resolve a section type to the family adapter function that decomposes it.
 * Falls back to the universal generic adapter for unknown / unmapped types.
 *
 * @param {string} sectionType
 * @returns {(sectionTypeOrData: (string|object), options?: object) => object}
 */
export function resolveAdapter(sectionType) {
  return ADAPTERS[familyForType(sectionType)] || genericToCanvas;
}

/** @deprecated alias of `resolveAdapter` (kept for earlier callers). */
export const adapterForType = resolveAdapter;

/** True when any own adapter explicitly handles `sectionType` (not just the generic fallback). */
export function handlesType(sectionType) {
  const type = typeof sectionType === 'string' ? sectionType.trim() : '';
  if (!type) return false;
  const family = familyForType(type);
  if (family === 'generic') return GENERIC_TYPES.includes(type);
  const coverage = ADAPTER_TYPE_COVERAGE[family];
  // chrome/custom/dynamic also accept prefix-matched types beyond their lists.
  return (coverage && coverage.includes(type)) || family === 'chrome' || family === 'custom' || family === 'dynamic';
}

/**
 * Decompose a section into a normalized Canvas_Model by routing its type to the
 * right family adapter. Accepts EITHER a section-type string (the adapter looks
 * up the transcribed presetData) OR a preset DATA descriptor (used directly).
 *
 * @param {(string|object)} sectionTypeOrData a section type, or a descriptor
 *   carrying a `sectionType`
 * @param {object} [dataOrCtx] adapter options; `dataOrCtx.sectionType` overrides
 *   the routing type, `dataOrCtx.domain` selects the chrome/custom domain
 * @returns {object} a normalized Canvas_Model
 */
export function decomposeSection(sectionTypeOrData, dataOrCtx = {}) {
  const opts = dataOrCtx && typeof dataOrCtx === 'object' ? dataOrCtx : {};
  const fromString = typeof sectionTypeOrData === 'string' ? sectionTypeOrData.trim() : '';
  const fromData =
    sectionTypeOrData && typeof sectionTypeOrData === 'object' && typeof sectionTypeOrData.sectionType === 'string'
      ? sectionTypeOrData.sectionType.trim()
      : '';
  const optType = typeof opts.sectionType === 'string' ? opts.sectionType.trim() : '';
  const sectionType = optType || fromString || fromData || 'generic';
  const adapter = resolveAdapter(sectionType);
  return adapter(sectionTypeOrData, opts);
}

export default decomposeSection;
